import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { AdfElementDetail } from './AdfElementDetail';
import { AdfXmlPreview, generateExampleXml } from './AdfXmlPreview';
import { AdfXmlValidator } from './AdfXmlValidator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronRight, Play, ExternalLink, Layers, Code2, Car, Users, History, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAdfFieldsByElement, type AdfElementNode } from '@/hooks/useAdfStandard';
import { useAdfChangelog } from '@/hooks/useAdfChangelog';

interface AdfSchemaViewerProps {
  element: AdfElementNode;
  elementTree: AdfElementNode[];
  onElementSelect: (slug: string) => void;
}

export function AdfSchemaViewer({ element, elementTree, onElementSelect }: AdfSchemaViewerProps) {
  const [activeTab, setActiveTab] = useState('structure');
  const [includeTradeIn, setIncludeTradeIn] = useState(false);
  const [includeCoBuyer, setIncludeCoBuyer] = useState(false);
  
  // Fetch field mappings for this element
  const { data: fieldMappings, isLoading: fieldsLoading } = useAdfFieldsByElement(element.id);
  
  // Fetch changelog from database
  const { data: changelog, isLoading: changelogLoading } = useAdfChangelog();

  const hasChildren = element.children && element.children.length > 0;
  const hasFields = fieldMappings && fieldMappings.length > 0;

  // Generate data-driven example XML for the root ADF element (for validator)
  const rootExampleXml = useMemo(() => {
    const rootElement = elementTree.find(e => e.tag_name === 'adf');
    if (!rootElement) return undefined;
    const xml = generateExampleXml(rootElement, { 
      currentDepth: 0, 
      maxDepth: 10, 
      requiredOnly: false,
      includeTradeIn,
      includeCoBuyer,
    });
    return `<?xml version="1.0" encoding="UTF-8"?>\n<?adf version="1.0"?>\n${xml}`;
  }, [elementTree, includeTradeIn, includeCoBuyer]);

  // Check if viewing root element (for changelog tab)
  const isRootElement = element.tag_name === 'adf';

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Element Header */}
      <AdfElementDetail element={element} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="w-full sm:w-auto inline-flex">
            <TabsTrigger value="structure" className="text-xs sm:text-sm flex items-center gap-1.5">
              <Layers className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Structure</span>
              <span className="sm:hidden">Struct</span>
            </TabsTrigger>
            <TabsTrigger value="example" className="text-xs sm:text-sm flex items-center gap-1.5">
              <Code2 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Example XML</span>
              <span className="sm:hidden">XML</span>
            </TabsTrigger>
            <TabsTrigger value="validate" className="text-xs sm:text-sm flex items-center gap-1.5">
              <Play className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Validate</span>
            </TabsTrigger>
            {isRootElement && (
              <TabsTrigger value="changelog" className="text-xs sm:text-sm flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Changelog</span>
                <span className="sm:hidden">Log</span>
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* Structure Tab */}
        <TabsContent value="structure" className="mt-4 space-y-6">
          {/* Child Elements */}
          {hasChildren && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Child Elements</h3>
                <Badge variant="outline" className="text-xs">
                  {element.children.length} element{element.children.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              
              {/* Desktop table */}
              <div className="hidden md:block border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-1/4">Element</TableHead>
                      <TableHead className="w-1/6 text-center">Required</TableHead>
                      <TableHead className="w-1/6 text-center">Repeatable</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {element.children.map((child) => (
                      <TableRow 
                        key={child.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => onElementSelect(child.slug)}
                      >
                        <TableCell>
                          <code className="font-mono text-sm text-primary">
                            &lt;{child.tag_name}&gt;
                          </code>
                        </TableCell>
                        <TableCell className="text-center">
                          {child.is_required ? (
                            <Badge className="bg-green-500/10 text-green-600 text-xs">Yes</Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">No</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {child.is_repeatable ? (
                            <Badge className="bg-blue-500/10 text-blue-600 text-xs">Yes</Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">No</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {child.description || child.display_name}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-2">
                {element.children.map((child) => (
                  <MobileChildCard 
                    key={child.id} 
                    child={child} 
                    onSelect={() => onElementSelect(child.slug)} 
                  />
                ))}
              </div>
            </div>
          )}

          {/* Field Mappings */}
          {fieldsLoading ? (
            <div className="text-sm text-muted-foreground">Loading field mappings...</div>
          ) : hasFields ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Mapped Fields</h3>
                <Badge variant="outline" className="text-xs">
                  {fieldMappings.length} field{fieldMappings.length !== 1 ? 's' : ''}
                </Badge>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-1/3">Field Name</TableHead>
                      <TableHead className="w-1/6">Type</TableHead>
                      <TableHead className="w-1/6 text-center">Required</TableHead>
                      <TableHead>ADF Tag</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fieldMappings.map((mapping) => (
                      <TableRow key={mapping.id}>
                        <TableCell>
                          <Link 
                            to={`/docs/field-library/${mapping.field?.slug}`}
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            {mapping.field?.name}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs font-mono">
                            {mapping.field?.data_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {mapping.is_required ? (
                            <Badge className="bg-green-500/10 text-green-600 text-xs">Yes</Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">No</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {mapping.field?.adf_tag ? (
                            <code className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                              &lt;{mapping.field.adf_tag}&gt;
                            </code>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {fieldMappings.some((m) => m.notes) && (
                <div className="text-xs text-muted-foreground mt-2">
                  <strong>Notes:</strong>
                  <ul className="list-disc list-inside mt-1">
                    {fieldMappings
                      .filter((m) => m.notes)
                      .map((m) => (
                        <li key={m.id}>{m.field?.name}: {m.notes}</li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}

          {/* Empty state */}
          {!hasChildren && !hasFields && (
            <div className="text-center py-8 text-muted-foreground">
              <p>This element has no child elements or field mappings defined.</p>
              {element.example_value && (
                <p className="mt-2">
                  Example value: <code className="bg-muted px-2 py-1 rounded">{element.example_value}</code>
                </p>
              )}
            </div>
          )}
        </TabsContent>

        {/* Example Tab */}
        <TabsContent value="example" className="mt-4 space-y-4">
          {/* Toggle Options */}
          <div className="flex flex-wrap gap-4 p-3 bg-muted/50 rounded-lg border">
            <div className="flex items-center space-x-2">
              <Switch
                id="include-trade-in"
                checked={includeTradeIn}
                onCheckedChange={setIncludeTradeIn}
              />
              <Label htmlFor="include-trade-in" className="flex items-center gap-1.5 cursor-pointer text-sm">
                <Car className="h-4 w-4 text-muted-foreground" />
                Include Trade-In Vehicle
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="include-co-buyer"
                checked={includeCoBuyer}
                onCheckedChange={setIncludeCoBuyer}
              />
              <Label htmlFor="include-co-buyer" className="flex items-center gap-1.5 cursor-pointer text-sm">
                <Users className="h-4 w-4 text-muted-foreground" />
                Include Co-Buyer
              </Label>
            </div>
          </div>

          {/* XML Preview */}
          <AdfXmlPreview 
            element={element} 
            includeTradeIn={includeTradeIn}
            includeCoBuyer={includeCoBuyer}
          />
        </TabsContent>

        {/* Validate Tab */}
        <TabsContent value="validate" className="mt-4">
          <AdfXmlValidator elementTree={elementTree} exampleXml={rootExampleXml} />
        </TabsContent>

        {/* Changelog Tab (root element only) */}
        {isRootElement && (
          <TabsContent value="changelog" className="mt-4 space-y-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <History className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">Version History</h3>
              </div>
              <p className="text-muted-foreground text-sm">
                Track changes and improvements to the ADF specification over time.
              </p>
            </div>

            {changelogLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : changelog && changelog.length > 0 ? (
              <div className="space-y-6">
                {changelog.map((entry) => (
                  <div key={entry.id} className="border rounded-lg overflow-hidden">
                    <div className="bg-muted/50 px-4 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge 
                          variant={entry.is_current ? 'default' : 'secondary'}
                          className="font-mono"
                        >
                          {entry.version}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {entry.release_date === '2000-01-01' 
                            ? 'Original' 
                            : format(new Date(entry.release_date), 'MMMM yyyy')}
                        </span>
                      </div>
                      {entry.is_current && (
                        <Badge variant="outline" className="text-xs bg-green-500/10 text-green-600 border-green-500/20">
                          Current
                        </Badge>
                      )}
                    </div>
                    <div className="p-4">
                      <ul className="space-y-2">
                        {entry.changes.map((change, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <span className="text-primary mt-1">•</span>
                            <span>{change}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No changelog entries available.
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// Mobile child element card
function MobileChildCard({ child, onSelect }: { child: AdfElementNode; onSelect: () => void }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="border rounded-lg bg-card overflow-hidden">
        <CollapsibleTrigger className="w-full p-3 text-left" onClick={onSelect}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ChevronRight className="h-4 w-4 text-primary" />
              <code className="font-mono text-sm text-primary">&lt;{child.tag_name}&gt;</code>
            </div>
            <div className="flex items-center gap-1">
              {child.is_required && (
                <Badge className="bg-green-500/10 text-green-600 text-xs">Req</Badge>
              )}
              {child.is_repeatable && (
                <Badge className="bg-blue-500/10 text-blue-600 text-xs">Rep</Badge>
              )}
            </div>
          </div>
          {child.description && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2 pl-6">
              {child.description}
            </p>
          )}
        </CollapsibleTrigger>
      </div>
    </Collapsible>
  );
}
